<script>
/**
 * @component LabVitalsTable
 * @description Muestra indicadores de laboratorio en tabla compacta con
 * barra de referencia verde/amarillo/rojo, sparkline de tendencia e
 * historial expandible por indicador. Los datos vienen del mismo
 * objeto vitals que usa VitalBox (grouped by name).
 * @prop {Object} vitals - { [name]: VitalTask[] } solo con displayType 'table'
 */
import { formatAR } from "@/services/datetime";

/** @description Grupos de laboratorio con sus nombres de vitales */
const LAB_GROUPS = {
  Hemograma: [
    "Hemoglobina (H)",
    "Hemoglobina (M)",
    "Hematocrito (H)",
    "Hematocrito (M)",
    "Glóbulos Blancos",
    "Plaquetas",
  ],
  Metabólico: [
    "Glucemia",
    "HbA1c",
    "Urea",
    "Creatinina",
    "Ácido Úrico",
    "Insulina",
  ],
  Lípidos: [
    "Colesterol Total",
    "Colesterol HDL (H)",
    "Colesterol HDL (M)",
    "Colesterol LDL",
    "Triglicéridos",
  ],
  Hepático: ["GPT (ALAT)", "GOT (ASAT)", "Bilirrubina Total"],
  Tiroides: ["TSH", "T4 Libre"],
  Próstata: ["PSA"],
};

export default {
  name: "LabVitalsTable",

  props: {
    /** @type {Object} Vitales agrupados por nombre */
    vitals: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      /** @description IDs de filas expandidas { [name]: boolean } */
      expanded: {},
    };
  },

  computed: {
    /**
     * @description Agrupa los vitales disponibles en secciones de laboratorio.
     * Solo incluye grupos que tienen al menos un vital con datos.
     * @returns {Array<{group: string, items: Array}>}
     */
    groupedSections() {
      const sections = [];
      for (const [group, names] of Object.entries(LAB_GROUPS)) {
        const items = names
          .filter((name) => this.vitals[name] && this.vitals[name].length)
          .map((name) => this.buildItem(name, this.vitals[name]));
        if (items.length) sections.push({ group, items });
      }
      // Vitales de lab sin grupo conocido
      const knownNames = Object.values(LAB_GROUPS).flat();
      const unknown = Object.keys(this.vitals)
        .filter((name) => !knownNames.includes(name))
        .map((name) => this.buildItem(name, this.vitals[name]));
      if (unknown.length) sections.push({ group: "Otros", items: unknown });
      return sections;
    },
  },

  methods: {
    /**
     * @description Construye el objeto de item para una fila de la tabla.
     * @param {string} name - Nombre del vital
     * @param {Array} tasks - Array de VitalTask ordenados por completedAt desc
     * @returns {Object}
     */
    buildItem(name, tasks) {
      const sorted = [...tasks].sort(
        (a, b) =>
          new Date(b.completedAt || b.createdAt) -
          new Date(a.completedAt || a.createdAt)
      );
      const latest = sorted[0];
      const value = latest?.answerValues?.[0]?.value;
      const unit = latest?.answerValues?.[0]?.name || "";
      const measurement =
        latest?.answerValues?.[0]?.measurement || latest?.measurement;
      const status =
        latest?.completedMeasurement || this.calcStatus(value, measurement);

      return {
        name,
        value,
        unit,
        status,
        measurement,
        date: latest?.completedAt || latest?.createdAt,
        history: sorted.map((t) => ({
          date: t.completedAt || t.createdAt,
          value: t.answerValues?.[0]?.value,
          unit: t.answerValues?.[0]?.name || "",
          status:
            t.completedMeasurement ||
            this.calcStatus(t.answerValues?.[0]?.value, measurement),
        })),
        trend: this.calcTrend(sorted),
      };
    },

    /**
     * @description Calcula estado green/yellow/red según valor y rangos.
     * @param {number} value
     * @param {Object} measurement
     * @returns {string}
     */
    calcStatus(value, measurement) {
      if (!value || !measurement) return "";
      const v = parseFloat(value);
      if (
        measurement.green &&
        v >= measurement.green.min &&
        v <= measurement.green.max
      )
        return "green";
      if (
        measurement.yellow &&
        v >= measurement.yellow.min &&
        v <= measurement.yellow.max
      )
        return "yellow";
      return "red";
    },

    /**
     * @description Calcula tendencia comparando últimas dos mediciones.
     * @param {Array} sorted - Tasks ordenadas desc
     * @returns {{ label: string, dir: string }}
     */
    calcTrend(sorted) {
      if (sorted.length < 2) return { label: "Sin datos prev.", dir: "none" };
      const v1 = parseFloat(sorted[0]?.answerValues?.[0]?.value);
      const v2 = parseFloat(sorted[1]?.answerValues?.[0]?.value);
      if (isNaN(v1) || isNaN(v2)) return { label: "—", dir: "none" };
      const diff = v1 - v2;
      if (Math.abs(diff) < 0.01) return { label: "→ Estable", dir: "stable" };
      return diff > 0
        ? { label: "↑ Subiendo", dir: "up" }
        : { label: "↓ Bajando", dir: "down" };
    },

    /**
     * @description Calcula porcentaje de posición del marcador en la barra de referencia.
     * @param {number} value
     * @param {Object} measurement
     * @returns {number} 0-100
     */
    /**
     * @description Calcula posición del marcador relativa al rango verde centrado.
     * La barra tiene 5 segmentos fijos: rojo(10%)-amarillo(8%)-verde(44%)-amarillo(8%)-rojo(30%)
     * El marcador se mapea proporcionalmente dentro del rango green.min → green.max.
     * @param {number} value
     * @param {Object} measurement
     * @returns {number} 0-100
     */
    markerPercent(value, measurement) {
      if (!measurement?.green) return 50;
      const v = parseFloat(value);
      const gMin = measurement.green.min;
      const gMax = measurement.green.max;
      const gRange = gMax - gMin || 1;
      const greenLeft = 18;
      const greenRight = 62;
      const ratio = (v - gMin) / gRange;
      const pos = greenLeft + ratio * (greenRight - greenLeft);
      return Math.min(98, Math.max(2, pos));
    },

    /**
     * @description Formatea fecha a dd/MM/yy
     * @param {string|Date} date
     * @returns {string}
     */
    /**
     * @description UC-REG-079: TZ del tenant.
     */
    formatDate(date) {
      if (!date) return "—";
      return formatAR(date, "DD/MM/YY");
    },

    /**
     * @description Alterna el historial expandido de una fila
     * @param {string} name
     */
    toggleExpanded(name) {
      this.$set(this.expanded, name, !this.expanded[name]);
    },

    /**
     * @description Calcula las posiciones de las barras de referencia como porcentajes.
     * @param {Object} measurement - { green: {min,max}, yellow: {min,max}, red: {min,max} }
     * @returns {Object|null}
     */
    barSegments(measurement) {
      if (!measurement?.green) return null;
      const totalMin = measurement.red?.min ?? 0;
      const totalMax = measurement.red?.max ?? measurement.green.max * 1.5;
      const range = totalMax - totalMin || 1;

      const pct = (v) =>
        Math.min(100, Math.max(0, ((v - totalMin) / range) * 100));

      const greenLeft = pct(measurement.green.min);
      const greenRight = pct(measurement.green.max);

      const yellowLowLeft = measurement.yellow
        ? pct(measurement.yellow.min)
        : 0;
      const yellowLowRight = measurement.yellow
        ? pct(measurement.green.min)
        : 0;

      const yellowHighLeft = greenRight;
      const yellowHighRight = measurement.yellow
        ? pct(measurement.yellow.max)
        : greenRight;

      return {
        redLow: { left: 0, width: yellowLowLeft },
        yellowLow: {
          left: yellowLowLeft,
          width: yellowLowRight - yellowLowLeft,
        },
        green: { left: greenLeft, width: greenRight - greenLeft },
        yellowHigh: {
          left: yellowHighLeft,
          width: yellowHighRight - yellowHighLeft,
        },
        redHigh: { left: yellowHighRight, width: 100 - yellowHighRight },
      };
    },

    /**
     * @description Genera puntos SVG para el sparkline de los últimos 6 valores
     * @param {Array} history
     * @returns {string}
     */
    sparkPoints(history) {
      const pts = history.slice(0, 6).reverse();
      if (pts.length < 2) return "";
      const values = pts
        .map((p) => parseFloat(p.value))
        .filter((v) => !isNaN(v));
      const minV = Math.min(...values);
      const maxV = Math.max(...values);
      const range = maxV - minV || 1;
      const w = 60,
        h = 20,
        pad = 2;
      return pts
        .map((p, i) => {
          const v = parseFloat(p.value);
          const x = (i / (pts.length - 1)) * (w - pad * 2) + pad;
          const y = h - pad - ((v - minV) / range) * (h - pad * 2);
          return `${x.toFixed(1)},${y.toFixed(1)}`;
        })
        .join(" ");
    },

    /**
     * @description Color del sparkline según tendencia
     * @param {string} dir
     * @returns {string}
     */
    sparkColor(dir) {
      if (dir === "up") return "#E24B4A";
      if (dir === "down") return "#1D9E75";
      return "#888780";
    },

    /**
     * @description Color de texto según estado
     * @param {string} status
     * @returns {string}
     */
    statusColor(status) {
      if (status === "green") return "#0F6E56";
      if (status === "yellow") return "#BA7517";
      if (status === "red") return "#A32D2D";
      return "var(--color-text-primary)";
    },

    /**
     * @description Color de barra según estado
     * @param {string} status
     * @returns {string}
     */
    barColor(status) {
      if (status === "green") return "#1D9E75";
      if (status === "yellow") return "#EF9F27";
      return "#E24B4A";
    },
  },
};
</script>

<template lang="pug">
.lab-vitals-table
  .lvt-section(v-for="section in groupedSections" :key="section.group")
    .lvt-group-title {{ section.group }}
    .lvt-header
      span Fecha
      span Indicador
      span Valor
      span Unidad
      span Referencia
      span Tendencia
      span

    .lvt-row-wrap(
      v-for="item in section.items"
      :key="item.name"
    )
      .lvt-row(@click="toggleExpanded(item.name)")
        span.lvt-date {{ formatDate(item.date) }}
        span.lvt-name {{ item.name }}
        span.lvt-value(:style="{ color: statusColor(item.status) }") {{ item.value }}
        span.lvt-unit {{ item.unit }}
        .lvt-ref
          .lvt-bar-outer
            span.lvt-marker-val(
              v-if="item.measurement"
              :style="{ left: markerPercent(item.value, item.measurement) + '%', color: statusColor(item.status) }"
            ) {{ item.value }}
            .lvt-bar-track
              .lvt-bar-solid(:style="{ width: '100%', background: barColor(item.status) }")
          .lvt-range-wrap(v-if="item.measurement")
            span.lvt-range-label {{ item.measurement.green?.min }}
            span.lvt-range-label {{ item.measurement.green?.max }}
        .lvt-trend
          svg(
            v-if="item.history.length > 1"
            width="60" height="20" viewBox="0 0 60 20"
          )
            polyline(
              :points="sparkPoints(item.history)"
              fill="none"
              :stroke="sparkColor(item.trend.dir)"
              stroke-width="1.5"
              stroke-linejoin="round"
            )
            circle(
              v-if="sparkPoints(item.history)"
              cx="58" cy="10" r="2"
              :fill="sparkColor(item.trend.dir)"
            )
          span.lvt-trend-label(:class="item.trend.dir") {{ item.trend.label }}
        span.lvt-chevron {{ expanded[item.name] ? '▴' : '▾' }}

      .lvt-history(v-show="expanded[item.name]")
        .lvt-history-title Historial
        .lvt-history-row(
          v-for="(h, i) in item.history"
          :key="i"
        )
          span.lvt-history-date {{ formatDate(h.date) }}
          span.lvt-history-val(:style="{ color: statusColor(h.status) }") {{ h.value }}
          span.lvt-history-unit {{ h.unit }}
          .lvt-history-bar
            .lvt-history-fill(
              :style="{ width: markerPercent(h.value, item.measurement) + '%', background: barColor(h.status) }"
            )
</template>

<style lang="scss" scoped>
.lab-vitals-table {
  font-family: "Plus Jakarta Sans", sans-serif;
}

.lvt-section {
  margin-bottom: 20px;
}

.lvt-group-title {
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 6px;
}

.lvt-header {
  display: grid;
  grid-template-columns: 62px 160px 70px 50px 1fr 100px 20px;
  gap: 10px;
  padding: 0 12px 4px;

  span {
    font-size: 10px;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}

.lvt-row-wrap {
  border: 0.5px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 4px;
  background: white;
}

.lvt-row {
  display: grid;
  grid-template-columns: 62px 160px 70px 50px 1fr 100px 20px;
  align-items: center;
  gap: 10px;
  padding: 7px 12px;
  cursor: pointer;

  &:hover {
    background: #f8fafc;
  }
}

.lvt-date {
  font-size: 12px;
  color: #64748b;
  white-space: nowrap;
}

.lvt-name {
  font-size: 13px;
  font-weight: 500;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.lvt-value {
  font-size: 15px;
  font-weight: 500;
}

.lvt-unit {
  font-size: 11px;
  color: #94a3b8;
}

.lvt-ref {
  .lvt-bar-outer {
    position: relative;
    padding-top: 14px;
    margin-bottom: 2px;
  }

  .lvt-marker-val {
    position: absolute;
    top: 0;
    font-size: 10px;
    font-weight: 700;
    transform: translateX(-50%);
    white-space: nowrap;
  }

  .lvt-bar-track {
    position: relative;
    height: 6px;
    border-radius: 3px;
    overflow: hidden;
  }

  .lvt-bar-solid {
    height: 6px;
    border-radius: 3px;
  }

  .lvt-range-wrap {
    display: flex;
    justify-content: space-between;
    margin-top: 4px;
  }

  .lvt-range-label {
    font-size: 9px;
    color: #94a3b8;
  }
}

.lvt-trend {
  display: flex;
  align-items: center;
  gap: 5px;

  .lvt-trend-label {
    font-size: 11px;
    font-weight: 500;

    &.up {
      color: #a32d2d;
    }
    &.down {
      color: #0f6e56;
    }
    &.stable,
    &.none {
      color: #94a3b8;
    }
  }
}

.lvt-chevron {
  font-size: 11px;
  color: #94a3b8;
  text-align: center;
  user-select: none;
}

.lvt-history {
  border-top: 0.5px solid #f1f5f9;
  padding: 8px 12px 10px;
  background: #f8fafc;
}

.lvt-history-title {
  font-size: 10px;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
}

.lvt-history-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 0;
  border-bottom: 0.5px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }
}

.lvt-history-date {
  font-size: 12px;
  color: #64748b;
  min-width: 58px;
}

.lvt-history-val {
  font-size: 13px;
  font-weight: 500;
  min-width: 44px;
}

.lvt-history-unit {
  font-size: 11px;
  color: #94a3b8;
  min-width: 36px;
}

.lvt-history-bar {
  flex: 1;
  height: 4px;
  border-radius: 2px;
  background: #e2e8f0;
  position: relative;

  .lvt-history-fill {
    position: absolute;
    height: 4px;
    border-radius: 2px;
    left: 0;
  }
}
</style>
